import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Users, TrendingUp, Building2, Lock, ArrowLeft, ExternalLink, Calendar, Clock, Zap } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import analytics from '@/utils/analytics';

const API = window.location.origin + '/api';

// Format subscriber count (1200000 -> 1.2M)
const formatSubscribers = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Calculate days ago
const daysAgo = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  return `${Math.floor(diff / 30)} months ago`;
};

// Check if item is new (detected within last 7 days)
const isNewItem = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  return diffDays <= 7;
};

// Check if item is very recent (within 48 hours)
const isVeryRecent = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  return diffHours <= 48;
};

// Helper to get trust signal label based on mention count
const getTrustSignalLabel = (mentionCount) => {
  if (mentionCount >= 3) return 'Frequently featured across videos';
  if (mentionCount === 2) return 'Repeated sponsor';
  return 'Recently detected';
};

const BrandProfile = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Logged-in users get FULL access (no paywall until payment integration)
  const FREE_LIMIT = isLoggedIn ? 100 : 3;

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.user_id) {
          setIsLoggedIn(true);
        }
      } catch (error) {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    loadBrandData();
  }, [brandId]);

  const loadBrandData = async () => {
    setLoading(true);
    try {
      // Add cache-busting timestamp to force fresh fetch
      const timestamp = Date.now();
      
      // Fetch all brands with cache-busting
      const brandsRes = await axios.get(`${API}/admin/brands?limit=100&_t=${timestamp}`);
      const allBrands = brandsRes.data.brands || [];
      
      // Find brand by ID or URL-encoded name
      const decodedId = decodeURIComponent(brandId);
      const foundBrand = allBrands.find(b => 
        b.detected_brand_id === decodedId || 
        b.brand_name.toLowerCase() === decodedId.toLowerCase() ||
        b.brand_name_normalized === decodedId.toLowerCase()
      );
      
      if (foundBrand) {
        setBrand(foundBrand);
        setLastRefreshed(new Date());
        
        // Track brand view
        analytics.brandClick(foundBrand.brand_name, foundBrand.detected_brand_id, 'brand_profile');
        
        // Fetch creators that work with this brand
        const channelNames = foundBrand.detected_from_channels || [];
        
        // Fetch all creators with cache-busting
        const creatorsRes = await axios.get(`${API}/creators?limit=100&_t=${timestamp}`);
        const allCreators = creatorsRes.data.creators || [];
        
        // Match creators by channel name
        const matchedCreators = allCreators.filter(c => 
          channelNames.some(name => 
            name.toLowerCase().includes(c.channel_name?.toLowerCase()) ||
            c.channel_name?.toLowerCase().includes(name.toLowerCase())
          )
        );
        
        // If no matches found, create placeholder data from channel names
        if (matchedCreators.length === 0 && channelNames.length > 0) {
          const placeholderCreators = channelNames.map((name, idx) => ({
            creator_id: `channel_${idx}`,
            channel_name: name,
            subscriber_count: null,
            niche: null,
            youtube_link: null,
            mention_count: Math.max(1, Math.floor(foundBrand.mention_count / channelNames.length)),
            first_detected_at: foundBrand.first_detected_at,
            last_detected_at: foundBrand.last_detected_at
          }));
          
          // Sort by last_detected_at DESC, then mention_count DESC
          placeholderCreators.sort((a, b) => {
            const dateA = new Date(a.last_detected_at || 0);
            const dateB = new Date(b.last_detected_at || 0);
            if (dateB - dateA !== 0) return dateB - dateA;
            return (b.mention_count || 0) - (a.mention_count || 0);
          });
          
          setCreators(placeholderCreators);
        } else {
          // Add mention count and timestamps to matched creators
          const creatorsWithMentions = matchedCreators.map(c => ({
            ...c,
            mention_count: Math.max(1, Math.floor(foundBrand.mention_count / Math.max(1, matchedCreators.length))),
            first_detected_at: c.first_detected_at || foundBrand.first_detected_at,
            last_detected_at: c.last_analyzed_at || foundBrand.last_detected_at
          }));
          
          // Sort by last_detected_at DESC (newest first), then mention_count DESC
          creatorsWithMentions.sort((a, b) => {
            const dateA = new Date(a.last_detected_at || 0);
            const dateB = new Date(b.last_detected_at || 0);
            if (dateB - dateA !== 0) return dateB - dateA;
            return (b.mention_count || 0) - (a.mention_count || 0);
          });
          
          setCreators(creatorsWithMentions);
        }
      }
    } catch (error) {
      console.error('Failed to load brand:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatorClick = (creator) => {
    if (creator.creator_id && !creator.creator_id.startsWith('channel_')) {
      analytics.creatorClick(creator.channel_name, creator.creator_id, 'brand_profile');
      navigate(`/creator/${creator.creator_id}`);
    }
  };

  const handleUnlock = () => {
    setShowUnlockModal(true);
    analytics.ctaClick('unlock_creators', 'brand_profile');
  };

  const handleRefresh = () => {
    loadBrandData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading brand profile...</p>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Brand Not Found</h1>
          <p className="text-slate-600 mb-8">We couldn't find this brand in our database.</p>
          <Link to="/explore-brands">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse All Brands
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const visibleCreators = creators.slice(0, FREE_LIMIT);
  const lockedCreators = creators.slice(FREE_LIMIT);
  const hasLockedContent = lockedCreators.length > 0;
  
  // Check if brand is new
  const brandIsNew = isNewItem(brand.first_detected_at);
  const brandIsVeryRecent = isVeryRecent(brand.first_detected_at);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`${brand.brand_name} - Sponsorship Data`}
        description={`See which YouTube creators ${brand.brand_name} sponsors. Discover sponsorship relationships and find similar brands.`}
        path={`/brand/${brandId}`}
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" />
                All Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">Discover Creators</Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Brand Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link to="/explore-brands" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Brands
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="brand-name">
                      {brand.brand_name}
                    </h1>
                    {brandIsVeryRecent && (
                      <Badge className="bg-emerald-500 text-white text-xs animate-pulse">
                        <Zap className="w-3 h-3 mr-1" />
                        Just Added
                      </Badge>
                    )}
                    {brandIsNew && !brandIsVeryRecent && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                        New
                      </Badge>
                    )}
                  </div>
                  {brand.niche_category && (
                    <Badge className="mt-1 bg-slate-100 text-slate-700 font-normal">
                      {brand.niche_category}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                {brand.mention_count || 0}
              </p>
              <p className="text-sm text-slate-600">sponsorship mentions</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-4 text-sm text-slate-600">
            {/* Data source proof */}
            <span className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              Detected from video mentions
            </span>
            {brand.last_detected_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Last detected {daysAgo(brand.last_detected_at)}
              </span>
            )}
            {lastRefreshed && (
              <span className="flex items-center gap-1 text-slate-400">
                <Clock className="w-4 h-4" />
                Data refreshed {daysAgo(lastRefreshed.toISOString())}
              </span>
            )}
          </div>
          
          {/* Insight - Seen across X creators */}
          {brand.detected_from_channels && brand.detected_from_channels.length > 0 && (
            <p className="mt-3 text-sm text-indigo-600 font-medium">
              Seen across {brand.detected_from_channels.length} creator{brand.detected_from_channels.length > 1 ? 's' : ''}
            </p>
          )}
          
          {/* Example video link */}
          {brand.sample_videos && brand.sample_videos[0] && (
            <a 
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(brand.sample_videos[0] + ' ' + (brand.detected_from_channels?.[0] || ''))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 text-sm text-slate-500 hover:text-indigo-600 inline-block"
            >
              View example video →
            </a>
          )}
          
          {/* Inline data disclaimer */}
          <p className="mt-4 text-xs text-slate-400">
            Data inferred from public YouTube content. Does not imply official partnership.
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Creators Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
              Creators Sponsored by {brand.brand_name}
            </h2>
            <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-slate-500 hover:text-slate-700">
              <Clock className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
          <p className="text-slate-600 mb-6">
            YouTube creators who have worked with this brand (sorted by most recent)
          </p>

          {creators.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Analyzing Creator Relationships</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                We're building the relationship data for this brand. Check back soon for updates.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Visible Creators */}
              {visibleCreators.map((creator, index) => {
                const creatorIsNew = isNewItem(creator.first_detected_at);
                const creatorIsVeryRecent = isVeryRecent(creator.first_detected_at);
                
                return (
                  <Card 
                    key={creator.creator_id || index}
                    className={`p-5 hover:shadow-md transition-shadow ${creator.creator_id && !creator.creator_id.startsWith('channel_') ? 'cursor-pointer' : ''} ${creatorIsVeryRecent ? 'ring-2 ring-emerald-200 bg-emerald-50/30' : ''}`}
                    onClick={() => handleCreatorClick(creator)}
                    data-testid={`creator-card-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {creator.channel_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                              {creator.channel_name}
                            </h3>
                            {creatorIsVeryRecent && (
                              <Badge className="bg-emerald-500 text-white text-xs">
                                <Zap className="w-3 h-3 mr-0.5" />
                                New
                              </Badge>
                            )}
                            {creatorIsNew && !creatorIsVeryRecent && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {creator.subscriber_count && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {formatSubscribers(creator.subscriber_count)} subscribers
                              </span>
                            )}
                            {creator.niche && (
                              <span>• {creator.niche}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                          {creator.mention_count || 1}
                        </p>
                        <p className="text-xs text-slate-500">mentions</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {getTrustSignalLabel(creator.mention_count || 1)}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* Locked Creators */}
              {hasLockedContent && (
                <div className="relative">
                  <div className="space-y-4 filter blur-sm pointer-events-none">
                    {lockedCreators.slice(0, 2).map((creator, index) => (
                      <Card key={`locked-${index}`} className="p-5 opacity-60">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                            <div>
                              <div className="h-5 w-32 bg-slate-200 rounded mb-2"></div>
                              <div className="h-4 w-24 bg-slate-100 rounded"></div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="h-6 w-8 bg-slate-200 rounded"></div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Unlock CTA Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
                    <div className="text-center p-6">
                      <Lock className="w-10 h-10 text-indigo-600 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        +{lockedCreators.length} more creators
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        Unlock full creator list for this brand
                      </p>
                      <Button 
                        onClick={handleUnlock}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="unlock-creators-btn"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        View All Creators
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Sample Videos */}
        {brand.sample_videos && brand.sample_videos.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Featured In
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {brand.sample_videos.slice(0, 4).map((video, index) => (
                <a 
                  key={index}
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(video)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <p className="text-slate-700 line-clamp-2 hover:text-indigo-600">{video}</p>
                  </Card>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>
            Find More Brands Like {brand.brand_name}
          </h3>
          <p className="text-indigo-100 mb-6">
            Discover other {brand.niche_category || 'brands'} sponsoring YouTube creators
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/explore-brands">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6">
                Explore All Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6">
                Find Creators
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <Lock className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Premium Feature</h3>
              <p className="text-slate-600 mb-6">
                Get full access to all creator relationships, detailed analytics, and advanced filters.
              </p>
              <div className="space-y-3">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                  Upgrade to Pro
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => setShowUnlockModal(false)}
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default BrandProfile;
